import type { CurrentSession, EffectivePermissions, Staff } from '@elite-residence/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, Fragment, type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { initials, PageHead } from '../ui/design';
import { Icon } from '../ui/icons';
import {
  Alert,
  Badge,
  Field,
  failureOf,
  Loading,
  QueryFailure,
  ROLE_LABELS,
  readResponse,
  useAction,
  useFailureMessage,
} from '../ui/ui';
import { groupPermissions, permissionLabel } from './permissionCatalog';

// Team administration (FR-17; V1-1-10; T-05 commands; AD-1). The server
// decides every action (permission, Branch Scope, CEO protection PD-10.47,
// last active CEO Q3-7). Buttons are shown from the session's own
// permissions only as a convenience. No re-authentication (C-3 rejected).

const ROLES = ['ceo', 'manager', 'accountant', 'booking_responsible', 'operations'] as const;

const text: Dictionary<
  | 'team'
  | 'addStaff'
  | 'name'
  | 'email'
  | 'role'
  | 'scope'
  | 'allBranches'
  | 'oneBranch'
  | 'branch'
  | 'state'
  | 'active'
  | 'deactivated'
  | 'create'
  | 'created'
  | 'emailInUse'
  | 'noStaff'
  | 'back'
  | 'access'
  | 'permission'
  | 'source'
  | 'granted'
  | 'notGranted'
  | 'roleDefault'
  | 'individualGrant'
  | 'individualRevoke'
  | 'grant'
  | 'revoke'
  | 'clear'
  | 'reason'
  | 'reasonRequired'
  | 'changeRole'
  | 'changeScope'
  | 'deactivate'
  | 'reactivate'
  | 'invite'
  | 'invited'
  | 'credentialExists'
  | 'mfaReset'
  | 'mfaResetHint'
  | 'mfaResetDone'
  | 'mfaNone'
  | 'lastCeo'
  | 'apply'
  | 'saved'
  | 'reviewPermissions'
  | 'reviewHint'
  | 'override'
  | 'keepDefault'
  | 'retry'
> = {
  en: {
    team: 'Team & permissions',
    addStaff: 'Add Staff member',
    name: 'Display name',
    email: 'Email (sign-in identity)',
    role: 'Role',
    scope: 'Branch Scope',
    allBranches: 'All Branches',
    oneBranch: 'One Branch',
    branch: 'Branch',
    state: 'Account',
    active: 'Active',
    deactivated: 'Deactivated',
    create: 'Create',
    created: 'Staff member created. Send the invitation from their page.',
    reviewPermissions: 'Review permissions',
    reviewHint:
      'The role gives a starting template. Grant or revoke individual permissions where you are allowed to; the table shows what this employee will actually have.',
    override: 'Change',
    keepDefault: 'Role default',
    emailInUse: 'This email is already used by another Staff member.',
    noStaff: 'No Staff members in your scope.',
    retry: 'Try again',
    back: 'Back to Team',
    access: 'Effective permissions',
    permission: 'Permission',
    source: 'Source',
    granted: 'Granted',
    notGranted: 'Not granted',
    roleDefault: 'Role default',
    individualGrant: 'Individual Grant',
    individualRevoke: 'Individual Revoke',
    grant: 'Grant',
    revoke: 'Revoke',
    clear: 'Clear',
    reason: 'Reason',
    reasonRequired: 'A reason is required.',
    changeRole: 'Change role',
    changeScope: 'Change Branch Scope',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    invite: 'Send invitation email',
    invited: 'Invitation sent. The link is valid for 24 hours.',
    credentialExists: 'This Staff member already has a password; use password reset instead.',
    mfaReset: 'Reset two-factor authentication',
    mfaResetHint:
      'Ends all their sessions and removes their authenticator and recovery codes. They set up two-factor authentication again at the next sign-in.',
    mfaResetDone: 'Two-factor authentication was reset.',
    mfaNone: 'This Staff member has no two-factor authentication to reset.',
    lastCeo: 'The last active CEO cannot be deactivated or demoted.',
    apply: 'Apply',
    saved: 'Saved.',
  },
  ar: {
    team: 'الفريق والصلاحيات',
    addStaff: 'إضافة موظف',
    name: 'الاسم الظاهر',
    email: 'البريد الإلكتروني (هوية تسجيل الدخول)',
    role: 'الدور',
    scope: 'نطاق الفروع',
    allBranches: 'جميع الفروع',
    oneBranch: 'فرع واحد',
    branch: 'الفرع',
    state: 'الحساب',
    active: 'نشط',
    deactivated: 'معطّل',
    create: 'إنشاء',
    created: 'تم إنشاء الموظف. أرسل الدعوة من صفحته.',
    reviewPermissions: 'مراجعة الصلاحيات',
    reviewHint:
      'يمنح الدور قالبًا مبدئيًا. امنح أو اسحب صلاحيات فردية حيث يُسمح لك؛ يعرض الجدول ما سيملكه هذا الموظف فعليًا.',
    override: 'تغيير',
    keepDefault: 'افتراضي الدور',
    emailInUse: 'هذا البريد مستخدم لموظف آخر.',
    noStaff: 'لا يوجد موظفون ضمن نطاقك.',
    retry: 'إعادة المحاولة',
    back: 'العودة إلى الفريق',
    access: 'الصلاحيات الفعلية',
    permission: 'الصلاحية',
    source: 'المصدر',
    granted: 'ممنوحة',
    notGranted: 'غير ممنوحة',
    roleDefault: 'افتراضية للدور',
    individualGrant: 'منح فردي',
    individualRevoke: 'سحب فردي',
    grant: 'منح',
    revoke: 'سحب',
    clear: 'إزالة',
    reason: 'السبب',
    reasonRequired: 'السبب مطلوب.',
    changeRole: 'تغيير الدور',
    changeScope: 'تغيير نطاق الفروع',
    deactivate: 'تعطيل',
    reactivate: 'إعادة تفعيل',
    invite: 'إرسال بريد الدعوة',
    invited: 'تم إرسال الدعوة. الرابط صالح لمدة 24 ساعة.',
    credentialExists: 'لدى هذا الموظف كلمة مرور بالفعل؛ استخدم إعادة تعيين كلمة المرور.',
    mfaReset: 'إعادة تعيين التحقق بخطوتين',
    mfaResetHint:
      'ينهي جميع جلساته ويزيل تطبيق المصادقة ورموز الاسترداد. يعيد إعداد التحقق بخطوتين عند تسجيل الدخول التالي.',
    mfaResetDone: 'تمت إعادة تعيين التحقق بخطوتين.',
    mfaNone: 'لا يوجد لدى هذا الموظف تحقق بخطوتين لإعادة تعيينه.',
    lastCeo: 'لا يمكن تعطيل آخر رئيس تنفيذي نشط أو تخفيض دوره.',
    apply: 'تطبيق',
    saved: 'تم الحفظ.',
  },
};

function useBranches() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['property', 'branches'],
    queryFn: async ({ signal }) => {
      const r = await client.listBranches({}, { signal });
      return readResponse(r).branches;
    },
  });
}

function ScopeLabel({ scope }: { readonly scope: Staff['scope'] }) {
  const t = useText(text);
  const branches = useBranches();
  if (scope.type === 'all_branches') return <>{t.allBranches}</>;
  const branch = branches.data?.find((b) => b.id === scope.branchId);
  return <>{branch ? `${branch.code} — ${branch.name}` : t.oneBranch}</>;
}

function ScopeFields({
  value,
  onChange,
}: {
  readonly value: { type: 'one_branch' | 'all_branches'; branchId?: string };
  readonly onChange: (scope: { type: 'one_branch' | 'all_branches'; branchId?: string }) => void;
}) {
  const t = useText(text);
  const branches = useBranches();
  return (
    <>
      <QueryFailure query={branches} />
      <Field id="scope-type" label={t.scope}>
        <select
          id="scope-type"
          value={value.type}
          onChange={(e) => onChange({ type: e.target.value as 'one_branch' | 'all_branches' })}
        >
          <option value="all_branches">{t.allBranches}</option>
          <option value="one_branch">{t.oneBranch}</option>
        </select>
      </Field>
      {value.type === 'one_branch' && (
        <Field id="scope-branch" label={t.branch}>
          <select
            id="scope-branch"
            value={value.branchId ?? ''}
            onChange={(e) => onChange({ type: 'one_branch', branchId: e.target.value })}
          >
            <option value="" />
            {(branches.data ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} — {b.name}
              </option>
            ))}
          </select>
        </Field>
      )}
    </>
  );
}

const modelText: Dictionary<
  | 'sub'
  | 'role'
  | 'roleHint'
  | 'permission'
  | 'permissionHint'
  | 'scope'
  | 'scopeHint'
  | 'members'
  | 'templates'
  | 'templatesHint'
  | 'templatesLoad'
> = {
  en: {
    sub: 'Access is the role template, adjusted per person, within a branch scope. The server checks every action.',
    role: 'Role',
    roleHint: 'The job: CEO, Manager, Accountant, Booking Responsible or Operations. It sets the starting template.',
    permission: 'Permission',
    permissionHint: 'What the person may do: the role template, plus individual grants or revokes with a reason.',
    scope: 'Branch scope',
    scopeHint: 'Where the person may do it: all branches, or one.',
    members: 'Team',
    templates: 'Role templates',
    templatesHint:
      'The starting permissions of each role, as the server defines them. Individual changes are made per person.',
    templatesLoad: 'Role templates could not be loaded.',
  },
  ar: {
    sub: 'الوصول هو قالب الدور، مع تعديلات لكل شخص، ضمن نطاق فرع. الخادم يتحقق من كل إجراء.',
    role: 'الدور',
    roleHint: 'الوظيفة: الرئيس التنفيذي أو المدير أو المحاسب أو مسؤول الحجز أو العمليات. يحدد القالب الابتدائي.',
    permission: 'الصلاحية',
    permissionHint: 'ما يستطيعه الشخص: قالب الدور، مع منح أو سحب فردي مع ذكر السبب.',
    scope: 'نطاق الفرع',
    scopeHint: 'أين يستطيع ذلك: كل الفروع أو فرع واحد.',
    members: 'الفريق',
    templates: 'قوالب الأدوار',
    templatesHint: 'الصلاحيات الابتدائية لكل دور كما يحددها الخادم. التعديلات الفردية تتم لكل شخص.',
    templatesLoad: 'تعذر تحميل قوالب الأدوار.',
  },
};

// One permission table, grouped by area with readable names; the code
// stays visible for support and audit.
function GroupedPermissionRows<T extends { readonly code: string }>({
  rows,
  columns,
  render,
}: {
  readonly rows: readonly T[];
  readonly columns: number;
  readonly render: (row: T) => ReactNode;
}) {
  const { locale } = useLocale();
  return (
    <tbody>
      {groupPermissions(rows).map((group) => (
        <Fragment key={group.key}>
          <tr className="er-pgroup">
            <th scope="rowgroup" colSpan={columns}>
              {group.label[locale]}
            </th>
          </tr>
          {group.rows.map((row) => (
            <tr key={row.code}>
              <td>
                <div className="er-cell-main">{permissionLabel(row.code, locale)}</div>
                <div className="er-cell-sub er-mono">{row.code}</div>
              </td>
              {render(row)}
            </tr>
          ))}
        </Fragment>
      ))}
    </tbody>
  );
}

function AccessModel() {
  const m = useText(modelText);
  const part = (n: number, title: string, hint: string) => (
    <div className="er-model__part">
      <span className="er-num">{n}</span>
      <div>
        <b className="er-model__title">{title}</b>
        <span>{hint}</span>
      </div>
    </div>
  );
  return (
    <div className="er-model">
      {part(1, m.role, m.roleHint)}
      <span className="er-model__plus" aria-hidden="true">
        +
      </span>
      {part(2, m.permission, m.permissionHint)}
      <span className="er-model__plus" aria-hidden="true">
        +
      </span>
      {part(3, m.scope, m.scopeHint)}
    </div>
  );
}

// Read-only role-template matrix from previewStaffPermissions (team.invite):
// each column is what the server grants a new member of that role.
function RoleTemplates() {
  const client = useApiClient();
  const { locale } = useLocale();
  const m = useText(modelText);
  const templates = useQuery({
    queryKey: ['team', 'role-templates'],
    staleTime: 300_000,
    queryFn: async ({ signal }) => {
      const result: Record<string, Set<string>> = {};
      let codes: string[] = [];
      for (const role of ROLES) {
        const r = await client.previewStaffPermissions({ body: { role, grants: [], revokes: [] } }, { signal });
        const permissions = readResponse(r).permissions;
        codes = permissions.map((p) => p.code);
        result[role] = new Set(permissions.filter((p) => p.granted).map((p) => p.code));
      }
      return { codes, result };
    },
  });
  if (templates.isPending) return <Loading />;
  if (templates.isError) return <Alert kind="error">{m.templatesLoad}</Alert>;
  const { codes, result } = templates.data;
  return (
    <section className="er-panel">
      <div className="er-panel__head">
        <h2 className="er-panel__title">{m.templates}</h2>
        <span className="er-cell-sub">{m.templatesHint}</span>
      </div>
      <div className="er-tablewrap" style={{ border: 0 }}>
        <table className="er-matrix" aria-label={m.templates}>
          <thead>
            <tr>
              <th>{m.permission}</th>
              {ROLES.map((role) => (
                <th key={role} className="er-matrix__c">
                  {ROLE_LABELS[role]?.[locale] ?? role}
                </th>
              ))}
            </tr>
          </thead>
          <GroupedPermissionRows
            rows={codes.map((code) => ({ code }))}
            columns={ROLES.length + 1}
            render={(row) =>
              ROLES.map((role) => (
                <td key={role} className="er-matrix__c">
                  {result[role]?.has(row.code) ? (
                    <span className="er-yes" role="img" aria-label={locale === 'ar' ? 'ممنوحة' : 'Granted'}>
                      <Icon name="check" />
                    </span>
                  ) : (
                    <span className="er-no" role="img" aria-label={locale === 'ar' ? 'غير ممنوحة' : 'Not granted'}>
                      —
                    </span>
                  )}
                </td>
              ))
            }
          />
        </table>
      </div>
    </section>
  );
}

export function TeamPage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const queries = useQueryClient();
  const failureMessage = useFailureMessage();
  const staff = useQuery({
    queryKey: ['team', 'staff'],
    queryFn: async ({ signal }) => {
      const r = await client.listStaff({ signal });
      if (r.status !== 200) return { ok: false as const, failure: failureOf(r) ?? { status: 500 } };
      return { ok: true as const, rows: r.body.staff };
    },
  });
  const staffRows = staff.data?.ok === true ? staff.data.rows : [];
  const staffFailure = staff.isError ? { status: 503 } : staff.data?.ok === false ? staff.data.failure : null;
  const canInvite = session.permissions.includes('team.invite');
  const canGrant = session.permissions.includes('team.permission_grant');
  const canRevoke = session.permissions.includes('team.permission_revoke');
  const [form, setForm] = useState({
    displayName: '',
    email: '',
    role: 'operations' as (typeof ROLES)[number],
    scope: { type: 'all_branches' } as { type: 'one_branch' | 'all_branches'; branchId?: string },
  });
  const action = useAction();
  const m = useText(modelText);
  const [view, setView] = useState<'members' | 'templates'>('members');
  const [done, setDone] = useState(false);
  // Individual overrides proposed for the new employee (code -> effect).
  const [overrides, setOverrides] = useState<Readonly<Record<string, 'grant' | 'revoke'>>>({});
  const grants = Object.keys(overrides)
    .filter((code) => overrides[code] === 'grant')
    .sort();
  const revokes = Object.keys(overrides)
    .filter((code) => overrides[code] === 'revoke')
    .sort();
  const preview = useQuery({
    queryKey: ['team', 'preview', form.role, grants.join(','), revokes.join(',')],
    enabled: canInvite,
    queryFn: async ({ signal }) => {
      const r = await client.previewStaffPermissions({ body: { role: form.role, grants, revokes } }, { signal });
      return readResponse(r).permissions;
    },
  });

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!preview.isSuccess) return;
    setDone(false);
    const result = await action.run(
      (key) =>
        client.createStaff({
          headers: { 'idempotency-key': key },
          body: {
            displayName: form.displayName,
            email: form.email,
            role: form.role,
            scope:
              form.scope.type === 'one_branch'
                ? { type: 'one_branch', branchId: form.scope.branchId ?? '' }
                : { type: 'all_branches' },
            ...(grants.length ? { grants } : {}),
            ...(revokes.length ? { revokes } : {}),
          },
        }),
      { email_in_use: t.emailInUse },
    );
    if (result) {
      setDone(true);
      setForm({ ...form, displayName: '', email: '' });
      setOverrides({});
      await queries.invalidateQueries({ queryKey: ['team'] });
    }
  }

  return (
    <main>
      <PageHead title={t.team} sub={m.sub} />
      <AccessModel />
      <nav className="er-tabs" aria-label={t.team}>
        <button
          type="button"
          className="er-tab"
          aria-current={view === 'members' ? 'page' : undefined}
          onClick={() => setView('members')}
        >
          {m.members}
        </button>
        {canInvite ? (
          <button
            type="button"
            className="er-tab"
            aria-current={view === 'templates' ? 'page' : undefined}
            onClick={() => setView('templates')}
          >
            {m.templates}
          </button>
        ) : null}
      </nav>
      {view === 'templates' ? <RoleTemplates /> : null}
      <section className="er-card" hidden={view !== 'members'}>
        {staff.isPending ? (
          <Loading />
        ) : staffFailure ? (
          <>
            <Alert kind="error">{failureMessage(staffFailure)}</Alert>
            <button
              className="er-button"
              type="button"
              disabled={staff.isFetching}
              onClick={() => {
                void staff.refetch();
              }}
            >
              {t.retry}
            </button>
          </>
        ) : staffRows.length === 0 ? (
          <p className="er-hint">{t.noStaff}</p>
        ) : (
          <table className="er-table">
            <thead>
              <tr>
                <th>{t.name}</th>
                <th>{t.role}</th>
                <th>{t.scope}</th>
                <th>{t.state}</th>
              </tr>
            </thead>
            <tbody>
              {staffRows.map((s) => (
                <tr key={s.id}>
                  <td>
                    <span className="er-person">
                      <span className="er-avatar" aria-hidden="true">
                        {initials(s.displayName)}
                      </span>
                      <Link to={`/team/${s.id}`}>{s.displayName}</Link>
                    </span>
                  </td>
                  <td>{ROLE_LABELS[s.role]?.[locale] ?? s.role}</td>
                  <td>
                    <ScopeLabel scope={s.scope} />
                  </td>
                  <td>
                    <Badge tone={s.accountState === 'active' ? 'ok' : 'off'}>
                      {s.accountState === 'active' ? t.active : t.deactivated}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {canInvite && view === 'members' && (
        <section className="er-card" aria-labelledby="add-staff">
          <h2 id="add-staff">{t.addStaff}</h2>
          {action.error && <Alert kind="error">{action.error}</Alert>}
          {done && <Alert kind="success">{t.created}</Alert>}
          <form onSubmit={create} noValidate>
            <Field id="staff-name" label={t.name}>
              <input
                id="staff-name"
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            </Field>
            <Field id="staff-email" label={t.email}>
              <input
                id="staff-email"
                type="email"
                dir="ltr"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field id="staff-role" label={t.role}>
              <select
                id="staff-role"
                value={form.role}
                onChange={(e) => {
                  setForm({ ...form, role: e.target.value as (typeof ROLES)[number] });
                  setOverrides({});
                }}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]?.[locale]}
                  </option>
                ))}
              </select>
            </Field>
            <ScopeFields value={form.scope} onChange={(scope) => setForm({ ...form, scope })} />
            <h3>{t.reviewPermissions}</h3>
            <p className="er-hint">{t.reviewHint}</p>
            {preview.isPending ? (
              <Loading />
            ) : preview.isError ? (
              <QueryFailure query={preview} />
            ) : (
              <table className="er-table" aria-label={t.reviewPermissions}>
                <thead>
                  <tr>
                    <th>{t.permission}</th>
                    <th>{t.granted}</th>
                    <th>{t.source}</th>
                    <th>{t.override}</th>
                  </tr>
                </thead>
                <GroupedPermissionRows
                  rows={preview.data ?? []}
                  columns={4}
                  render={(p) => (
                    <>
                      <td>
                        <Badge tone={p.granted ? 'ok' : 'off'}>
                          {p.granted ? `✓ ${t.granted}` : `— ${t.notGranted}`}
                        </Badge>
                      </td>
                      <td>
                        {p.source === 'role_default'
                          ? t.roleDefault
                          : p.source === 'individual_grant'
                            ? t.individualGrant
                            : p.source === 'individual_revoke'
                              ? t.individualRevoke
                              : t.notGranted}
                      </td>
                      <td>
                        <select
                          aria-label={`${t.override} ${p.code}`}
                          value={overrides[p.code] ?? ''}
                          onChange={(e) => {
                            const next = { ...overrides };
                            if (e.target.value === 'grant' || e.target.value === 'revoke')
                              next[p.code] = e.target.value;
                            else delete next[p.code];
                            setOverrides(next);
                          }}
                        >
                          <option value="">{t.keepDefault}</option>
                          {canGrant && <option value="grant">{t.grant}</option>}
                          {canRevoke && <option value="revoke">{t.revoke}</option>}
                        </select>
                      </td>
                    </>
                  )}
                />
              </table>
            )}
            <button
              className="er-button"
              type="submit"
              disabled={action.busy || !form.displayName || !form.email || !preview.isSuccess}
            >
              {t.create}
            </button>
          </form>
        </section>
      )}
    </main>
  );
}

function ReasonAction({
  label,
  danger,
  required,
  onSubmit,
}: {
  readonly label: string;
  readonly danger?: boolean;
  readonly required: boolean;
  readonly onSubmit: (reason: string) => Promise<void>;
}) {
  const t = useText(text);
  const [reason, setReason] = useState('');
  const id = `reason-${label.replace(/\W+/g, '-')}`;
  return (
    <form
      className="er-actions"
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit(reason.trim()).then(() => setReason(''));
      }}
    >
      <label className="er-visually-hidden" htmlFor={id}>
        {`${label} — ${t.reason}`}
      </label>
      <input id={id} placeholder={t.reason} value={reason} onChange={(e) => setReason(e.target.value)} />
      <button
        className={`er-button${danger ? ' danger' : ' secondary'}`}
        type="submit"
        disabled={required && !reason.trim()}
      >
        {label}
      </button>
    </form>
  );
}

export function StaffDetailPage({ session }: { readonly session: CurrentSession }) {
  const { staffId = '' } = useParams();
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const queries = useQueryClient();
  const action = useAction();
  const [notice, setNotice] = useState<string | null>(null);
  const can = (key: string) => session.permissions.includes(key);
  const access = useQuery({
    queryKey: ['team', 'access', staffId],
    queryFn: async ({ signal }) => {
      const r = await client.getStaffEffectivePermissions({ path: { staffId } }, { signal });
      return readResponse(r);
    },
  });
  const [role, setRole] = useState<string>('');
  const [scope, setScope] = useState<{ type: 'one_branch' | 'all_branches'; branchId?: string }>({
    type: 'all_branches',
  });
  const specific = {
    last_active_ceo: t.lastCeo,
    credential_exists: t.credentialExists,
    no_change: t.mfaNone,
  };

  async function after(ok: unknown, message = t.saved) {
    if (ok) {
      setNotice(message);
      await queries.invalidateQueries({ queryKey: ['team'] });
    }
  }

  if (access.isPending) return <Loading />;
  if (access.isError) return <QueryFailure query={access} />;
  const data: EffectivePermissions | null | undefined = access.data;
  if (!data) return <Alert kind="error">{t.back}</Alert>;
  const staff = data.staff;
  const headers = (key: string) => ({ 'idempotency-key': key });

  return (
    <main>
      <div className="er-crumbs">
        <Icon name="arrow" className="er-crumb-back" />
        <Link to="/team">{t.back}</Link>
      </div>
      <div className="er-pagehead">
        <span className="er-avatar er-avatar--lg" aria-hidden="true">
          {initials(staff.displayName)}
        </span>
        <div>
          <h1>{staff.displayName}</h1>
          <div className="er-pagesub">
            {ROLE_LABELS[staff.role]?.[locale] ?? staff.role} · <ScopeLabel scope={staff.scope} />
          </div>
        </div>
      </div>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      {notice && !action.error && <Alert kind="success">{notice}</Alert>}
      <section className="er-card">
        <dl>
          <dt>{t.email}</dt>
          <dd dir="ltr">{staff.email ?? '—'}</dd>
          <dt>{t.role}</dt>
          <dd>{ROLE_LABELS[staff.role]?.[locale] ?? staff.role}</dd>
          <dt>{t.scope}</dt>
          <dd>
            <ScopeLabel scope={staff.scope} />
          </dd>
          <dt>{t.state}</dt>
          <dd>
            <Badge tone={staff.accountState === 'active' ? 'ok' : 'off'}>
              {staff.accountState === 'active' ? t.active : t.deactivated}
            </Badge>
          </dd>
        </dl>
        <div className="er-actions">
          {can('team.invite') && staff.accountState === 'active' && (
            <button
              className="er-button secondary"
              type="button"
              disabled={action.busy}
              onClick={async () =>
                after(
                  await action.run((key) => client.inviteStaff({ path: { staffId }, headers: headers(key) }), specific),
                  t.invited,
                )
              }
            >
              {t.invite}
            </button>
          )}
        </div>
      </section>

      {can('team.role_assign') && (
        <section className="er-card" aria-labelledby="change-role">
          <h2 id="change-role">{t.changeRole}</h2>
          <Field id="new-role" label={t.role}>
            <select id="new-role" value={role || staff.role} onChange={(e) => setRole(e.target.value)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]?.[locale]}
                </option>
              ))}
            </select>
          </Field>
          <ReasonAction
            label={t.apply}
            required
            onSubmit={async (reason) =>
              after(
                await action.run(
                  (key) =>
                    client.changeStaffRole({
                      path: { staffId },
                      headers: headers(key),
                      body: { role: (role || staff.role) as (typeof ROLES)[number], reason },
                    }),
                  specific,
                ),
              )
            }
          />
        </section>
      )}

      {can('team.scope_assign') && (
        <section className="er-card" aria-labelledby="change-scope">
          <h2 id="change-scope">{t.changeScope}</h2>
          <ScopeFields value={scope} onChange={setScope} />
          <ReasonAction
            label={t.apply}
            required
            onSubmit={async (reason) =>
              after(
                await action.run(
                  (key) =>
                    client.changeStaffScope({
                      path: { staffId },
                      headers: headers(key),
                      body: {
                        scope:
                          scope.type === 'one_branch'
                            ? { type: 'one_branch', branchId: scope.branchId ?? '' }
                            : { type: 'all_branches' },
                        reason,
                      },
                    }),
                  specific,
                ),
              )
            }
          />
        </section>
      )}

      {can('team.deactivate') && (
        <section className="er-card">
          <h2>{staff.accountState === 'active' ? t.deactivate : t.reactivate}</h2>
          <ReasonAction
            label={staff.accountState === 'active' ? t.deactivate : t.reactivate}
            danger={staff.accountState === 'active'}
            required
            onSubmit={async (reason) => {
              const call = staff.accountState === 'active' ? client.deactivateStaff : client.reactivateStaff;
              after(
                await action.run(
                  (key) => call({ path: { staffId }, headers: headers(key), body: { reason } }),
                  specific,
                ),
              );
            }}
          />
        </section>
      )}

      {can('team.mfa_reset') && staff.accountState === 'active' && (
        <section className="er-card" aria-labelledby="mfa-reset">
          <h2 id="mfa-reset">{t.mfaReset}</h2>
          <p className="er-hint">{t.mfaResetHint}</p>
          <ReasonAction
            label={t.mfaReset}
            danger
            required
            onSubmit={async (reason) =>
              after(
                await action.run(
                  (key) => client.resetStaffMfa({ path: { staffId }, headers: headers(key), body: { reason } }),
                  specific,
                ),
                t.mfaResetDone,
              )
            }
          />
        </section>
      )}

      <section className="er-card" aria-labelledby="effective-access">
        <h2 id="effective-access">{t.access}</h2>
        <table className="er-table" aria-labelledby="effective-access">
          <thead>
            <tr>
              <th>{t.permission}</th>
              <th>{t.granted}</th>
              <th>{t.source}</th>
              <th />
            </tr>
          </thead>
          <GroupedPermissionRows
            rows={data.permissions}
            columns={4}
            render={(p) => (
              <>
                <td>
                  <Badge tone={p.granted ? 'ok' : 'off'}>{p.granted ? `✓ ${t.granted}` : `— ${t.notGranted}`}</Badge>
                </td>
                <td>
                  {p.source === 'role_default'
                    ? t.roleDefault
                    : p.source === 'individual_grant'
                      ? t.individualGrant
                      : p.source === 'individual_revoke'
                        ? t.individualRevoke
                        : t.notGranted}
                </td>
                <td>
                  <div className="er-actions">
                    {can('team.permission_grant') && !p.granted && (
                      <button
                        className="er-link-button"
                        type="button"
                        onClick={async () =>
                          after(
                            await action.run((key) =>
                              client.grantStaffPermission({
                                path: { staffId, permissionCode: p.code },
                                headers: headers(key),
                                body: {},
                              }),
                            ),
                          )
                        }
                      >
                        {t.grant}
                      </button>
                    )}
                    {can('team.permission_revoke') && p.granted && (
                      <button
                        className="er-link-button"
                        type="button"
                        onClick={async () =>
                          after(
                            await action.run((key) =>
                              client.revokeStaffPermission({
                                path: { staffId, permissionCode: p.code },
                                headers: headers(key),
                                body: {},
                              }),
                            ),
                          )
                        }
                      >
                        {t.revoke}
                      </button>
                    )}
                    {(p.source === 'individual_grant' || p.source === 'individual_revoke') && (
                      <button
                        className="er-link-button"
                        type="button"
                        onClick={async () =>
                          after(
                            await action.run((key) =>
                              client.clearStaffPermissionOverride({
                                path: { staffId, permissionCode: p.code },
                                headers: headers(key),
                                body: {},
                              }),
                            ),
                          )
                        }
                      >
                        {t.clear}
                      </button>
                    )}
                  </div>
                </td>
              </>
            )}
          />
        </table>
      </section>
    </main>
  );
}
